<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Tooltip Component
      p.text--secondary Provides contextual information when hovering, focusing, or clicking on elements. Built on top of Vuetify's v-tooltip with enhanced customization options.

    // Basic Usage
    .component-section.mb-8#basic-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Usage
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Tooltip
          p.text--secondary.caption Simple tooltip with default styling

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .d-flex.gap-4.align-center.justify-center.pa-4
            yz-tooltip(text="This is a basic tooltip")
              yz-btn(color="primary" label="Hover me")
            
            yz-tooltip(text="Tooltip with custom text content")
              yz-btn(outlined label="Another button")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === basicCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicCode ? 'success' : ''" @click="copyCode(basicCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicCode }}

    // Direction Variations
    .component-section.mb-8#direction-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('direction-variations')") Direction Variations
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('direction-variations')") Tooltip Directions
          p.text--secondary.caption Tooltips can be positioned in different directions

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 700px;")
          .d-flex.gap-4.align-center.justify-center.pa-6
            yz-tooltip(text="Top tooltip" direction="top")
              yz-btn(label="Top")
            
            yz-tooltip(text="Bottom tooltip" direction="bottom")
              yz-btn(secondary label="Bottom")
              
            yz-tooltip(text="Left tooltip" direction="left")
              yz-btn(outlined label="Left")
              
            yz-tooltip(text="Right tooltip" direction="right")
              yz-btn(underline label="Right")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === directionCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === directionCode ? 'success' : ''" @click="copyCode(directionCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ directionCode }}

    // Color Variations
    .component-section.mb-8#color-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-variations')") Color Variations
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('color-variations')") Tooltip Colors
          p.text--secondary.caption Different color options for tooltips

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.gap-4.align-center.justify-center.pa-6.flex-wrap
            yz-tooltip(text="Black tooltip (default)" color="black")
              yz-btn(color="primary" label="Black")
            
            yz-tooltip(text="Neutral color tooltip" color="neutral-500")
              yz-btn(secondary label="Neutral")
              
            yz-tooltip(text="Success color tooltip" color="success-500")
              yz-btn(color="success" outlined label="Success")
              
            yz-tooltip(text="Warning color tooltip" color="warning-500")
              yz-btn(color="warning" outlined label="Warning")
              
            yz-tooltip(text="Error color tooltip" color="danger-500")
              yz-btn(color="danger" label="Danger")
              
            yz-tooltip(text="Custom hex color" color="#9c27b0")
              yz-btn(outlined label="Hex Color")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === colorCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === colorCode ? 'success' : ''" @click="copyCode(colorCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ colorCode }}

    // Custom Content with Slots
    .component-section.mb-8#custom-slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('custom-slots')") Custom Content with Slots
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('custom-slots')") Tooltip and Activator Slots
          p.text--secondary.caption Using slots for custom tooltip content and activator elements

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.gap-4.align-center.justify-center.pa-6.flex-wrap
            yz-tooltip
              template(#tooltip)
                .pa-2
                  .font-weight-bold Custom Tooltip Content
                  .text-caption With multiple lines and formatting
                  .d-flex.align-center.mt-1
                    yz-icon(small color="yellow") mdi-star
                    span.ml-1 Rich content
              span.text-decoration-underline.cursor-pointer Custom Element
              
            yz-tooltip(text="Tooltip for icon")
              yz-icon(large color="primary") mdi-information
              
            yz-tooltip
              template(#tooltip)
                .pa-2.text-center
                  yz-icon(color="success") mdi-check-circle
                  div.mt-1 Action completed successfully!
              yz-chip(color="success" text-color="white") Success Status
              
            yz-tooltip(color="primary-500")
              template(#tooltip)
                .pa-3(style="max-width: 250px;")
                  .d-flex.align-center.mb-2
                    yz-icon(color="white" size="20") mdi-help-circle
                    span.ml-2.font-weight-bold.white--text Help Information
                  .white--text.text-caption.mb-2 This feature allows you to manage your settings efficiently.
                  .d-flex.justify-end
                    yz-btn(text x-small color="white") Learn More
              yz-btn(outlined size="sm" label="Need Help?")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === slotsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === slotsCode ? 'success' : ''" @click="copyCode(slotsCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ slotsCode }}

    // Advanced Configuration
    .component-section.mb-8#advanced-config
      h3.mb-4.cursor-pointer(@click="navigateToSection('advanced-config')") Advanced Configuration
      .variations-grid
        .variation-item.mb-6(v-for="variation in advancedVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.gap-4.align-center.justify-center.pa-6
              yz-tooltip(v-bind="variation.props" :text="variation.tooltipText")
                yz-btn(color="primary" :label="variation.buttonText")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // Props
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Slots
      h4.mb-3 Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  name: "Tooltip",
  data() {
    return {
      copiedCode: null,
      basicCode: `yz-tooltip(text="This is a basic tooltip")
  yz-btn(color="primary" label="Hover me")

yz-tooltip(text="Tooltip with custom text content")
  yz-btn(outlined label="Another button")`,
      directionCode: `yz-tooltip(text="Top tooltip" direction="top")
  yz-btn(label="Top")

yz-tooltip(text="Bottom tooltip" direction="bottom")
  yz-btn(secondary label="Bottom")

yz-tooltip(text="Left tooltip" direction="left")
  yz-btn(outlined label="Left")

yz-tooltip(text="Right tooltip" direction="right")
  yz-btn(underline label="Right")`,
      colorCode: `yz-tooltip(text="Black tooltip (default)" color="black")
  yz-btn(color="primary" label="Black")

yz-tooltip(text="Neutral color tooltip" color="neutral-500")
  yz-btn(secondary label="Neutral")

yz-tooltip(text="Success color tooltip" color="success-500")
  yz-btn(color="success" outlined label="Success")

yz-tooltip(text="Warning color tooltip" color="warning-500")
  yz-btn(color="warning" outlined label="Warning")

yz-tooltip(text="Error color tooltip" color="danger-500")
  yz-btn(color="danger" label="Danger")

yz-tooltip(text="Custom hex color" color="#9c27b0")
  yz-btn(outlined label="Hex Color")`,
      slotsCode: `// Custom tooltip content with rich formatting
yz-tooltip
  template(#tooltip)
    .pa-2
      .font-weight-bold Custom Tooltip Content
      .text-caption With multiple lines and formatting
      .d-flex.align-center.mt-1
        yz-icon(small color="yellow") mdi-star
        span.ml-1 Rich content
  span.text-decoration-underline.cursor-pointer Custom Element

// Simple tooltip with icon activator
yz-tooltip(text="Tooltip for icon")
  yz-icon(large color="primary") mdi-information

// Tooltip with success status and custom content
yz-tooltip
  template(#tooltip)
    .pa-2.text-center
      yz-icon(color="success") mdi-check-circle
      div.mt-1 Action completed successfully!
  yz-chip(color="success" text-color="white") Success Status

// Complex tooltip with multiple elements
yz-tooltip(color="primary-500")
  template(#tooltip)
    .pa-3(style="max-width: 250px;")
      .d-flex.align-center.mb-2
        yz-icon(color="white" size="20") mdi-help-circle
        span.ml-2.font-weight-bold.white--text Help Information
      .white--text.text-caption.mb-2 This feature allows you to manage your settings efficiently.
      .d-flex.justify-end
        yz-btn(text x-small color="white") Learn More
  yz-btn(outlined size="sm" label="Need Help?")`,
      advancedVariations: [
        {
          name: 'Open on Click',
          id: 'open-on-click',
          description: 'Tooltip that opens when clicked instead of hovered',
          props: {
            openOnClick: true,
            openOnHover: false
          },
          tooltipText: 'Click to open tooltip',
          buttonText: 'Click Me',
          code: `yz-tooltip(text="Click to open tooltip" :open-on-click="true" :open-on-hover="false")
  yz-btn(color="primary" label="Click Me")`
        },
        {
          name: 'Custom Delays',
          id: 'custom-delays',
          description: 'Tooltip with custom open and close delays',
          props: {
            openDelay: 500,
            closeDelay: 1000
          },
          tooltipText: 'Delayed tooltip (500ms open, 1000ms close)',
          buttonText: 'Slow Tooltip',
          code: `yz-tooltip(text="Delayed tooltip" :open-delay="500" :close-delay="1000")
  yz-btn(color="primary" label="Slow Tooltip")`
        },
        {
          name: 'Custom Width',
          id: 'custom-width',
          description: 'Tooltip with custom maximum and minimum width',
          props: {
            maxWidth: 200,
            minWidth: 150
          },
          tooltipText: 'This is a longer tooltip text that will wrap within the specified width constraints',
          buttonText: 'Constrained Width',
          code: `yz-tooltip(text="Long tooltip text..." :max-width="200" :min-width="150")
  yz-btn(color="primary" label="Constrained Width")`
        },
        {
          name: 'No Arrow',
          id: 'no-arrow',
          description: 'Tooltip without the arrow pointer',
          props: {
            noArrow: true
          },
          tooltipText: 'Tooltip without arrow',
          buttonText: 'No Arrow',
          code: `yz-tooltip(text="Tooltip without arrow" :no-arrow="true")
  yz-btn(color="primary" label="No Arrow")`
        }
      ],
      componentProps: [
        {
          name: 'text',
          type: 'String',
          default: '',
          description: 'The tooltip text content'
        },
        {
          name: 'color',
          type: 'String',
          default: 'black',
          description: 'Background color of the tooltip. Accepts hex colors (#ff0000), CSS variables (var(--color)), design system tokens with yz- prefix (yz-primary-500), or design system tokens without prefix (primary-500)'
        },
        {
          name: 'direction',
          type: 'String',
          default: 'top',
          description: 'Position of the tooltip: "top", "bottom", "left", "right"'
        },
        {
          name: 'contentClass',
          type: 'String',
          default: '',
          description: 'Custom CSS class for the tooltip content. If not provided, generates class based on color and direction'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the tooltip is disabled'
        },
        {
          name: 'noArrow',
          type: 'Boolean',
          default: 'false',
          description: 'When set to true, removes the arrow from the tooltip'
        },
        {
          name: 'openDelay',
          type: 'Number|String',
          default: '0',
          description: 'Delay in milliseconds before opening the tooltip'
        },
        {
          name: 'closeDelay',
          type: 'Number|String',
          default: '200',
          description: 'Delay in milliseconds before closing the tooltip'
        },
        {
          name: 'maxWidth',
          type: 'Number|String',
          default: '500',
          description: 'Maximum width of the tooltip in pixels'
        },
        {
          name: 'minWidth',
          type: 'Number|String',
          default: '0',
          description: 'Minimum width of the tooltip in pixels'
        },
        {
          name: 'nudgeBottom',
          type: 'Number|String',
          default: '0',
          description: 'Nudge the tooltip content down'
        },
        {
          name: 'nudgeLeft',
          type: 'Number|String',
          default: '0',
          description: 'Nudge the tooltip content left'
        },
        {
          name: 'nudgeRight',
          type: 'Number|String',
          default: '0',
          description: 'Nudge the tooltip content right'
        },
        {
          name: 'nudgeTop',
          type: 'Number|String',
          default: '0',
          description: 'Nudge the tooltip content up'
        },
        {
          name: 'nudgeWidth',
          type: 'Number|String',
          default: '0',
          description: 'Nudge the tooltip content width'
        },
        {
          name: 'offsetOverflow',
          type: 'Boolean',
          default: 'false',
          description: 'Causes the component to flip to the opposite side when repositioned due to overflow'
        },
        {
          name: 'openOnClick',
          type: 'Boolean',
          default: 'false',
          description: 'Whether to open tooltip on click'
        },
        {
          name: 'openOnFocus',
          type: 'Boolean',
          default: 'true',
          description: 'Whether to open tooltip on focus'
        },
        {
          name: 'openOnHover',
          type: 'Boolean',
          default: 'true',
          description: 'Whether to open tooltip on hover'
        },
        {
          name: 'zIndex',
          type: 'Number|String',
          default: 'undefined',
          description: 'The z-index used for the component'
        }
      ],
      componentSlots: [
        {
          name: 'default',
          description: 'The activator element that triggers the tooltip'
        },
        {
          name: 'tooltip',
          description: 'Custom content for the tooltip. Overrides the text prop when used'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: transparent;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>